// Sports Configuration Mapping for Match Creation
// This file contains all sport-specific configurations including rules, scoring, and defaults

export const SPORTS_CONFIG = {
  // Badminton configurations
  badminton: {
    catalog_id: 'badminton_singles',
    is_team_sport: false,
    defaults: { points_to_win: 21, win_by_margin: 2, max_points: 30 },
    options: {
      points_to_win: [
        { value: 11, label: '11 Points' },
        { value: 15, label: '15 Points' },
        { value: 21, label: '21 Points (Standard)' }
      ],
      win_by_margin: [
        { value: 0, label: 'No Margin' },
        { value: 1, label: '1 Point' },
        { value: 2, label: '2 Points (Standard)' }
      ],
      max_points: [
        { value: 25, label: '25 Points' },
        { value: 30, label: '30 Points (Standard)' },
        { value: 999, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Game' },
        { value: 3, label: 'Best of 3 (Standard)' },
        { value: 5, label: 'Best of 5' }
      ]
    }
  },
  // Volleyball configurations
  volleyball: {
    catalog_id: 'volleyball_6v6',
    is_team_sport: true,
    // FIVB: sets to 25 (deciding set to 15), win by 2, no points cap.
    defaults: { points_to_win: 25, win_by_margin: 2, max_points: 999, total_games: 5, games_to_win: 3 },
    options: {
      points_to_win: [
        { value: 15, label: '15 Points' },
        { value: 21, label: '21 Points' },
        { value: 25, label: '25 Points (Standard)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Point' },
        { value: 2, label: '2 Points (Standard)' }
      ],
      max_points: [
        { value: 999, label: 'No Limit (Standard)' }
      ],
      total_games: [
        { value: 1, label: 'Single Game' },
        { value: 3, label: 'Best of 3 (Quick)' },
        { value: 5, label: 'Best of 5 (Standard)' }
      ]
    }
  },
  // Basketball configurations
  basketball: {
    catalog_id: 'basketball_5v5',
    is_team_sport: true,
    // FIBA default: 4 quarters x 10 minutes, 5-minute overtime periods, no draws.
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, periods: 4, period_minutes: 10, overtime_minutes: 5 },
    options: {
      points_to_win: [
        { value: null, label: 'No Point Limit (Timed Game)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Point' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Game' }
      ],
      period_minutes: [
        { value: 10, label: '4 x 10 min (FIBA)' },
        { value: 12, label: '4 x 12 min (NBA)' }
      ]
    }
  },
  // Football configurations
  football: {
    catalog_id: 'football_11v11',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null },
    options: {
      points_to_win: [
        { value: null, label: 'No Point Limit (Timed Game)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Goal' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Game' }
      ]
    }
  },
  // Tennis configurations
  tennis: {
    catalog_id: 'tennis_singles',
    is_team_sport: false,
    defaults: { points_to_win: 6, win_by_margin: 2, max_points: null },
    options: {
      points_to_win: [
        { value: 4, label: '4 Games (Short Set)' },
        { value: 6, label: '6 Games (Standard)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Game' },
        { value: 2, label: '2 Games (Standard)' }
      ],
      max_points: [
        { value: 7, label: '7 Games (Tiebreak)' },
        { value: 999, label: 'No Limit' }
      ],
      total_games: [
        { value: 3, label: 'Best of 3 Sets' },
        { value: 5, label: 'Best of 5 Sets' }
      ]
    }
  },

  // 4. Handball - Team Sport
  handball: {
    catalog_id: 'handball_7v7',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 60 },
    options: {
      points_to_win: [
        { value: null, label: 'Goals (No Point Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Goal' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Match (60 min)' }
      ]
    },
    rules: {
      players_per_team: 7,
      duration: '2 halves × 30 minutes',
      scoring: 'Goal = 1 point',
      special: ['3 steps without dribble', 'No foot contact', '6-meter goal area for GK only']
    }
  },

  // 5. Field Hockey - Team Sport
  fieldhockey: {
    catalog_id: 'field_hockey_11v11',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 60 },
    options: {
      points_to_win: [
        { value: null, label: 'Goals (No Point Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Goal' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Match (4 quarters)' }
      ]
    },
    rules: {
      players_per_team: 11,
      duration: '4 quarters × 15 minutes',
      scoring: 'Goal = 1 point',
      special: ['Ball played only with stick', 'No feet', 'Penalty corners common']
    }
  },

  // 6. Ice Hockey - Team Sport
  icehockey: {
    catalog_id: 'ice_hockey_6v6',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 60 },
    options: {
      points_to_win: [
        { value: null, label: 'Goals (No Point Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Goal' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Match (3 periods)' }
      ]
    },
    rules: {
      players_per_team: 6,
      duration: '3 periods × 20 minutes',
      scoring: 'Goal = 1 point',
      special: ['Body checking allowed', 'Icing & offside rules', 'Power play after penalties']
    }
  },

  // 7. American Football - Team Sport
  americanfootball: {
    catalog_id: 'american_football_11v11',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 60 },
    options: {
      points_to_win: [
        { value: null, label: 'Points (No Set Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Point' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Game (4 quarters)' }
      ]
    },
    rules: {
      players_per_team: 11,
      duration: '4 quarters × 15 minutes',
      scoring: 'Touchdown = 6, Extra point = 1/2, Field goal = 3, Safety = 2',
      special: ['4 downs to gain 10 yards', 'Forward pass allowed once', 'Heavy tactical play']
    }
  },

  // 8. Baseball - Team Sport
  baseball: {
    catalog_id: 'baseball_9v9',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: null },
    options: {
      points_to_win: [
        { value: null, label: 'Runs (No Set Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Run' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Game (9 innings)' }
      ]
    },
    rules: {
      players_per_team: 9,
      duration: '9 innings (no clock)',
      scoring: 'Run = 1 point',
      special: ['3 outs per inning', 'Pitcher vs batter duel', 'Home run scores all runners']
    }
  },

  // 9. Athletics (Track & Field) - Individual Sport
  athletics: {
    catalog_id: 'athletics_individual',
    is_team_sport: false,
    defaults: { points_to_win: null, win_by_margin: null, max_points: null, total_games: 1, games_to_win: 1 },
    options: {
      points_to_win: [
        { value: null, label: 'Time/Distance Based' }
      ],
      win_by_margin: [
        { value: null, label: 'Performance Based' }
      ],
      max_points: [
        { value: null, label: 'Not Applicable' }
      ],
      total_games: [
        { value: 1, label: 'Single Event' }
      ]
    },
    rules: {
      players: 'Individual sport',
      scoring: 'Time (races) or Distance/height (field events)',
      special: ['False start rules', 'Lane discipline', 'Multiple rounds possible']
    }
  },

  // 11. Cricket - Team Sport
  cricket: {
    catalog_id: 'cricket_11v11',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1 },
    options: {
      points_to_win: [
        { value: null, label: 'Runs (No Set Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Run' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'T20 (~3 hrs)' },
        { value: 1, label: 'ODI (~8 hrs)' },
        { value: 1, label: 'Test (5 days)' }
      ]
    },
    rules: {
      players_per_team: 11,
      scoring: 'Run = 1 point, Boundary = 4 or 6',
      special: ['Batting & bowling innings', '10 wickets per innings', 'LBW, caught, bowled dismissals']
    }
  },

  // 13. Table Tennis - Individual/Doubles Sport
  tabletennis: {
    catalog_id: 'table_tennis_singles',
    is_team_sport: false,
    // ITTF: games to 11, win by 2, no cap (deuce from 10-10); best of 5 most common.
    defaults: { points_to_win: 11, win_by_margin: 2, max_points: 999, total_games: 5, games_to_win: 3 },
    options: {
      points_to_win: [
        { value: 11, label: '11 Points (Standard)' },
        { value: 21, label: '21 Points (Old Rules)' }
      ],
      win_by_margin: [
        { value: 2, label: '2 Points (Standard)' }
      ],
      max_points: [
        { value: 999, label: 'No Limit (Standard)' }
      ],
      total_games: [
        { value: 3, label: 'Best of 3' },
        { value: 5, label: 'Best of 5 (Standard)' },
        { value: 7, label: 'Best of 7' }
      ]
    },
    rules: {
      players: 'Singles or Doubles',
      scoring: 'Game = 11 points, Match = Best of 5 or 7',
      special: ['Serve changes every 2 points', 'Ball must bounce once per side']
    }
  },

  // 14. Rugby Union - Team Sport
  rugby: {
    catalog_id: 'rugby_15v15',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 80 },
    options: {
      points_to_win: [
        { value: null, label: 'Points (No Set Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Point' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Match (80 min)' }
      ]
    },
    rules: {
      players_per_team: 15,
      duration: '2 halves × 40 minutes',
      scoring: 'Try = 5, Conversion = 2, Penalty goal = 3, Drop goal = 3',
      special: ['Forward passes illegal', 'Tackling below shoulders', 'Scrum & lineout restarts']
    }
  },

  // 15. Netball - Team Sport
  netball: {
    catalog_id: 'netball_7v7',
    is_team_sport: true,
    defaults: { points_to_win: null, win_by_margin: 1, max_points: null, total_games: 1, games_to_win: 1, duration_minutes: 60 },
    options: {
      points_to_win: [
        { value: null, label: 'Goals (No Point Limit)' }
      ],
      win_by_margin: [
        { value: 1, label: '1 Goal' }
      ],
      max_points: [
        { value: null, label: 'No Limit' }
      ],
      total_games: [
        { value: 1, label: 'Single Match (4 quarters)' }
      ]
    },
    rules: {
      players_per_team: 7,
      duration: '4 quarters × 15 minutes',
      scoring: 'Goal = 1 point (only by shooters)',
      special: ['No dribbling', '3-second rule', 'Fixed positions']
    }
  },

  // 16. Swimming - Individual/Relay Sport
  swimming: {
    catalog_id: 'swimming_individual',
    is_team_sport: false,
    defaults: { points_to_win: null, win_by_margin: null, max_points: null, total_games: 1, games_to_win: 1 },
    options: {
      points_to_win: [
        { value: null, label: 'Time Based (Fastest Wins)' }
      ],
      win_by_margin: [
        { value: null, label: 'Time Based' }
      ],
      max_points: [
        { value: null, label: 'Not Applicable' }
      ],
      total_games: [
        { value: 1, label: 'Single Event' },
        { value: 4, label: 'Relay (4 swimmers)' }
      ]
    },
    rules: {
      players: 'Individual / Relay teams',
      scoring: 'Fastest time wins',
      special: ['Stroke-specific rules', 'False start disqualification', 'Touch wall to finish']
    }
  }
};

/**
 * Resolve a sport name (any case, spaces/underscores/hyphens) to its SPORTS_CONFIG
 * key, or null when the sport is unknown. More specific names are checked
 * before generic ones ("table tennis" before "tennis", "american football"
 * before "football", "ice hockey" before "hockey").
 */
export const getSportConfigKey = (sportName) => {
  const name = (sportName || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!name) return null;
  const compact = name.replace(/\s/g, '');

  if (compact.includes('tabletennis') || compact.includes('pingpong')) return 'tabletennis';
  if (compact.includes('americanfootball')) return 'americanfootball';
  if (compact.includes('icehockey')) return 'icehockey';
  if (compact.includes('fieldhockey') || name.includes('hockey')) return 'fieldhockey';
  if (name.includes('badminton')) return 'badminton';
  if (name.includes('volleyball')) return 'volleyball';
  if (name.includes('basketball')) return 'basketball';
  if (name.includes('netball')) return 'netball';
  if (name.includes('handball')) return 'handball';
  if (name.includes('baseball')) return 'baseball';
  if (name.includes('football') || name.includes('soccer')) return 'football';
  if (name.includes('tennis')) return 'tennis';
  if (name.includes('athletics') || name.includes('track')) return 'athletics';
  if (name.includes('cricket')) return 'cricket';
  if (name.includes('rugby')) return 'rugby';
  if (name.includes('swimming')) return 'swimming';
  return null;
};

// Sport configuration for a sport name, or null when the sport is unknown.
export const findSportConfig = (sportName) => {
  const key = getSportConfigKey(sportName);
  return key ? SPORTS_CONFIG[key] : null;
};

// Helper function to get sport configuration by sport name
export const getSportConfigByName = (sportName) =>
  // Fallback to badminton config
  findSportConfig(sportName) || SPORTS_CONFIG.badminton;

// Helper function to get current sport configuration options
export const getCurrentSportOptions = (sportName) => {
  const config = getSportConfigByName(sportName);
  return config?.options || SPORTS_CONFIG.badminton.options;
};

// Helper function to get sport defaults
export const getSportDefaults = (sportName) => {
  const config = getSportConfigByName(sportName);
  return config?.defaults || SPORTS_CONFIG.badminton.defaults;
};

// Helper function to check if sport is team-based
export const isTeamSport = (sportName) => {
  const config = getSportConfigByName(sportName);
  return config?.is_team_sport || false;
};

// Export individual sport configurations for specific use cases
export const {
  badminton: BADMINTON_CONFIG,
  volleyball: VOLLEYBALL_CONFIG,
  basketball: BASKETBALL_CONFIG,
  football: FOOTBALL_CONFIG,
  tennis: TENNIS_CONFIG,
  handball: HANDBALL_CONFIG,
  fieldhockey: FIELDHOCKEY_CONFIG,
  icehockey: ICEHOCKEY_CONFIG,
  americanfootball: AMERICANFOOTBALL_CONFIG,
  baseball: BASEBALL_CONFIG,
  athletics: ATHLETICS_CONFIG,
  cricket: CRICKET_CONFIG,
  tabletennis: TABLETENNIS_CONFIG,
  rugby: RUGBY_CONFIG,
  netball: NETBALL_CONFIG,
  swimming: SWIMMING_CONFIG
} = SPORTS_CONFIG;